import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp, setDoc, updateDoc, increment, runTransaction } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";
import { generateCnFInvoicePdf } from "../utils/pdfCnFInvoice";
import { buildHsnSummary } from "../utils/hsnSummary";
import { supabase } from "../supabase";

const SELLER_INFO = {
  name: "SUPREME ENTERPRISES",
  address1: "NO.24, 24 KHATHA KHATHA NO.227/3/7 NO 227/3/7",
  address2: "GROUND FLOOR, NEAR CHRYSALIS SCHOOL,",
  address3: "HORMAVU AGARA, BANGALORE - 560043",
  gstin: "29AFAPC3951P1Z6",
  state: "Karnataka",
  stateCode: "29"
};

function CnFToDistributorBilling() {
  const [userProfile, setUserProfile] = useState(null);
  const [distributorList, setDistributorList] = useState([]);
  const [skuMaster, setSkuMaster] = useState({});
  const [skuList, setSkuList] = useState([]);
  const [items, setItems] = useState({});
  const [selectedDistributorId, setSelectedDistributorId] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/"); return; }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        if (!["superadmin", "admin"].includes(profile.role)) {
          alert("Unauthorized access");
          navigate("/dashboard");
          return;
        }
        setUserProfile(profile);
        loadData();
      }
    });
    return () => unsub();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Load Distributors
      const distQ = query(collection(db, "partyMaster"));
      const distSnap = await getDocs(distQ);
      const distArr = distSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDistributorList(distArr);

      // 2. Load SKU Master
      const skuQ = query(collection(db, "skuMaster"));
      const skuSnap = await getDocs(skuQ);
      const smap = {};
      const slist = [];
      const itemMap = {};
      skuSnap.forEach(d => {
        const data = d.data();
        const skuKey = String(data.sku || data.Name || "").trim().toUpperCase();
        if (!skuKey) return;
        smap[skuKey] = data;
        slist.push(skuKey);
        itemMap[skuKey] = "";
      });
      
      slist.sort((a,b) => (Number(smap[a].sortorder) || 999) - (Number(smap[b].sortorder) || 999));

      setSkuMaster(smap);
      setSkuList(slist);
      setItems({});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const financialSummary = useMemo(() => {
    let tQty = 0, tBasic = 0, tGst = 0, tCess = 0, tGrand = 0;
    Object.entries(items).forEach(([sku, units]) => {
      const m = skuMaster[sku];
      if (!m) return;

      const bags = Number(units.bag || 0);
      const ctns = Number(units.ctn || 0);
      const pkts = Number(units.pkt || 0);
      const pcs = Number(units.pcs || 0);

      // Calculation Principle (Based on "working" table):
      // The fundamental unit for pricing is the PIECE (PCS).
      // 1 Packet = (pcsPerPacket) Pieces.
      // 1 Carton = (packetsPerCarton) * (pcsPerPacket) Pieces.
      // 1 Bag = (cartonsPerBag) * (packetsPerCarton) * (pcsPerPacket) Pieces.
      // Total Pieces = (bags * bag_cfg * ctn_cfg * pkt_cfg) + (ctns * ctn_cfg * pkt_cfg) + (pkts * pkt_cfg) + pcs

      const c_Bag_Cfg = Number(m.cartonsPerBag) || 1;
      const c_Ctn_Cfg = Number(m.packetsPerCarton) || 1;
      const c_Pkt_Cfg = Number(m.pcsPerPacket) || 1;

      const totalPkt = (bags * c_Bag_Cfg * c_Ctn_Cfg) + 
                       (ctns * c_Ctn_Cfg) + 
                       pkts + 
                       (pcs / c_Pkt_Cfg);
      
      const q_for_invoice = totalPkt / (c_Bag_Cfg * c_Ctn_Cfg); // Quantity in CTNS

      if (totalPkt <= 0) return;
      
      const rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
      const basic = totalPkt * rate;
      
      // TAX CALCULATION: 5% of Basic (Exclusive)
      const gstRate = Number(m.gst) || 5; 
      const gst = basic * (gstRate / 100);
      
      const cess = basic * (Number(m.cess || 0) / 100);
      const addl = totalPkt * (Number(m.addlcess || 0));
      const rebate = totalPkt * (Number(m.Rebate || 0));

      const lineTotal = basic + gst + cess + addl + rebate;

      tQty += q_for_invoice;
      tBasic += basic;
      tGst += gst;
      tCess += (cess + addl + rebate);
      tGrand += lineTotal;
    });
    return { tQty, tBasic, tGst, tCess, tGrand };
  }, [items, skuMaster]);

  const handleConfirmOrder = async () => {
    if (!selectedDistributorId) { alert("Please select a Distributor"); return; }
    if (financialSummary.tQty === 0) { alert("Please enter quantities"); return; }
    if (submitting) return;

    const ok = window.confirm("Confirm Invoicing to Distributor?");
    if (!ok) return;

    setSubmitting(true);
    try {
      const distributor = distributorList.find(d => d.id === selectedDistributorId);
      const cleanItems = {};
      Object.entries(items).forEach(([sku, units]) => {
          const skuUpper = String(sku).trim().toUpperCase();
          const m = skuMaster[skuUpper];
          const c_Bag = Number(m.cartonsPerBag) || 1;
          const c_Ctn = Number(m.packetsPerCarton) || 1;
          const c_Pkt = Number(m.pcsPerPacket) || 1;
          
          const totalPkt = (Number(units.bag || 0) * c_Bag * c_Ctn) + 
                           (Number(units.ctn || 0) * c_Ctn) + 
                           Number(units.pkt || 0) + 
                           (Number(units.pcs || 0) / c_Pkt);
          
          if (totalPkt > 0) cleanItems[skuUpper] = totalPkt;
      });

      // 1. Build specific C&F HSN Summary with unit breakdowns for PDF (MUST BE BEFORE orderData)
      const cnfHsnSum = {};
      Object.entries(items).forEach(([sku, units]) => {
          const m = skuMaster[sku];
          if (!m || !cleanItems[sku]) return;
          const hsn = (m.hsn || m.HSN || "24022090").trim();
          
          if (!cnfHsnSum[hsn]) {
              cnfHsnSum[hsn] = { name: m.Name, qty: 0, basic: 0, gstAmt: 0, breakdown: [] };
          }
          const row = cnfHsnSum[hsn];
          
          const c_B_C = Number(m.cartonsPerBag) || 1;
          const c_C_C = Number(m.packetsPerCarton) || 1;
          const c_P_C = Number(m.pcsPerPacket) || 1;

          const totalPkt = (Number(units.bag || 0) * c_B_C * c_C_C) + 
                           (Number(units.ctn || 0) * c_C_C) + 
                           Number(units.pkt || 0) + 
                           (Number(units.pcs || 0) / c_P_C);

          const qExport = totalPkt / (c_B_C * c_C_C);
          const rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
          const basic = totalPkt * rate;
          
          // TAX CALCULATION: 5% of Basic (Exclusive)
          const gstRate = Number(m.gst) || 5;
          const gst = basic * (gstRate / 100);
          
          row.qty += qExport;
          row.basic += basic;
          row.gstAmt += gst;
          
          const parts = [];
          if (units.bag) parts.push(`${units.bag} Bag`);
          if (units.ctn) parts.push(`${units.ctn} Ctn`);
          if (units.pkt) parts.push(`${units.pkt} Pkt`);
          if (units.pcs) parts.push(`${units.pcs} Pcs`);
          row.breakdown.push(`${m.sku}: ${parts.join(", ")}`);
      });

      // 2. Generate sequential Invoice ID SP-YYMM-0001 via transaction
      const now = new Date();
      const yy = String(now.getFullYear()).slice(-2);
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const currentMonthKey = `${yy}${mm}`;
      
      let orderId = "";
      await runTransaction(db, async (transaction) => {
          const counterRef = doc(db, "counters", "distributor_invoice");
          const counterSnap = await transaction.get(counterRef);
          
          let nextSeq = 1;
          if (counterSnap.exists()) {
              const data = counterSnap.data();
              if (data.monthKey === currentMonthKey) {
                  nextSeq = (data.lastSeq || 0) + 1;
              }
          }
          
          orderId = `SP-${currentMonthKey}-${String(nextSeq).padStart(4, "0")}`;
          transaction.set(counterRef, {
              monthKey: currentMonthKey,
              lastSeq: nextSeq
          });
      });

      const orderData = {
        clientId: userProfile.clientId,
        distributorId: selectedDistributorId,
        partytype: "distributor",
        creditLimit: "",
        opening_balance: "",
        partyId: selectedDistributorId,
        partyName: distributor?.name || "",
        address: distributor?.address || "",
        gstno: distributor?.gstno || "",
        items: cleanItems,
        totalQty: financialSummary.tQty,
        totalAmount: financialSummary.tGrand,
        source: "CNF_TO_DISTRIBUTOR",
        status: "APPROVED",
        createdAt: serverTimestamp(),
        approvedAt: serverTimestamp(),
        approvedDateOnly: now.toISOString().slice(0, 10),
        displayOrderId: orderId,
        financialSummary: financialSummary,
        hsnSummary: cnfHsnSum,
        // Save unit configs for this invoice instance
        unitConfigs: Object.keys(cleanItems).reduce((acc, sku) => {
            const m = skuMaster[sku];
            if (m) {
                acc[sku] = `1 BAG = ${Number(m.cartonsPerBag)||1} Ctn | 1 Ctn = ${Number(m.packetsPerCarton)||1} Pkt | 1 Pkt = ${Number(m.pcsPerPacket)||1} Pcs`;
            }
            return acc;
        }, {})
      };

      const docRef = await addDoc(collection(db, "distributor_invoices"), orderData);

      // 3. Update Stock (Properly nested for internal tracking)
      const todayStr = getISTDate();
      const distStockRef = doc(db, "distributor_stock_daily", `${selectedDistributorId}_${todayStr}`);
      
      // Ensure record exists
      await setDoc(distStockRef, { distributorId: selectedDistributorId, date: todayStr }, { merge: true });

      const invUpdates = {};
      Object.entries(cleanItems).forEach(([sku, qty]) => {
          invUpdates[`inventory.${sku}.received`] = increment(qty);
      });

      await updateDoc(distStockRef, { 
          ...invUpdates,
          updatedAt: serverTimestamp() 
      });

      // 3.5. Update C&F (Hub) Stock (The "Missing Link")
      const cnfStockRef = doc(db, "cnf_stock_daily", `${userProfile.clientId}_${todayStr}`);
      await setDoc(cnfStockRef, { clientId: userProfile.clientId, date: todayStr }, { merge: true });
      const cnfInvUpdates = {};
      Object.entries(cleanItems).forEach(([sku, qty]) => {
          cnfInvUpdates[`inventory.${sku}.sold`] = increment(qty);
      });
      await updateDoc(cnfStockRef, { ...cnfInvUpdates, updatedAt: serverTimestamp() });

      // 4. Generate PDF
      const pdf = generateCnFInvoicePdf(orderId, { ...orderData, ...distributor }, cnfHsnSum, financialSummary, SELLER_INFO);
      
      const blob = pdf.output("blob");

      // 5. Upload to Supabase (Parallelish)
      try {
          const fileName = `Invoice-${orderId}.pdf`;
          const { error } = await supabase.storage.from("invoices").upload(fileName, blob, { contentType: "application/pdf", upsert: true });
          if (!error) {
              const { data } = supabase.storage.from("invoices").getPublicUrl(fileName);
              if (data?.publicUrl) {
                  await updateDoc(docRef, { taxInvoicePdfUrl: data.publicUrl });
              }
          }
      } catch (uerr) { console.error("Supabase upload failed:", uerr); }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${orderId}.pdf`;
      link.click();

      alert("Invoice Generated Successfully!");
      navigate("/distributor-invoices");
    } catch (err) {
      console.error(err);
      alert("Error: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadExisting = () => {
      // This would require fetching a specific order, but since this is for "Confirm", the previous logic handles the immediate download.
      alert("Please confirm the order to download the active invoice.");
  };

  if (loading) return (
    <Layout>
      <div style={{ padding: 100, textAlign: "center", color: "var(--text-muted)" }}>
        Initializing C&F Billing Terminal...
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="layout-content">
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>DISTRIBUTOR INVOICE</h1>
                {/* description removed per user request */}
            </div>
            <div style={{ display: "flex", gap: 30 }}>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)" }}>{financialSummary.tQty.toFixed(2)}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Qty</div>
                </div>
                <div style={{ width: 1, height: 40, background: "var(--border)" }}></div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--success)" }}>₹{financialSummary.tGrand.toLocaleString()}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Invoice Value</div>
                </div>
            </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 32 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            <div className="premium-card">
              <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" }}>Target Distributor</label>
              <select 
                  className="form-input"
                  value={selectedDistributorId} 
                  onChange={e => setSelectedDistributorId(e.target.value)} 
                  style={{ width: "100%", padding: 14 }}
              >
                  <option value="">-- Select Distributor --</option>
                  {distributorList.map(d => <option key={d.id} value={d.id}>{d.name} ({d.place || "No Location"})</option>)}
              </select>
            </div>

            <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-main)" }}>PRODUCT LIST</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>PRICING: DISTRIBUTOR SCALE</span>
              </div>
              <div style={{ maxHeight: 600, overflowY: "auto" }}>
                <table className="enterprise-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Code</th>
                      <th>Description</th>
                      <th style={{ textAlign: "right" }}>Dist. Rate</th>
                      <th style={{ width: 120, textAlign: "center" }}>Qty</th>
                      <th style={{ textAlign: "right" }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skuList.map((sku) => {
                        const m = skuMaster[sku];
                        const rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
                        const iUnits = items[sku] || {};
                        
                        const c_Bag = Number(m.cartonsPerBag) || 1;
                        const c_Ctn = Number(m.packetsPerCarton) || 1;
                        const c_Pkt = Number(m.pcsPerPacket) || 1;

                        const totalPcs = (Number(iUnits.bag || 0) * c_Bag * c_Ctn * c_Pkt) + 
                                         (Number(iUnits.ctn || 0) * c_Ctn * c_Pkt) + 
                                         (Number(iUnits.pkt || 0) * c_Pkt) + 
                                         Number(iUnits.pcs || 0);
                        
                        const q = totalPcs / (c_Bag * c_Ctn * c_Pkt);
                        const sub = totalPcs * rate;

                        return (
                          <tr key={sku}>
                            <td style={{ fontWeight: 800, color: "var(--primary)", fontSize: 12 }}>{sku}</td>
                            <td>
                              <div style={{ fontWeight: 700, fontSize: 14 }}>{m.Name}</div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>MRP: ₹{m.mrp} | HSN: {m.hsn || m.HSN}</div>
                              <div style={{ fontSize: 10, color: "var(--primary)", marginTop: 4, fontWeight: 700 }}>
                                Config: 1 BAG = {c_Bag} Ctn | 1 Ctn = {c_Ctn} Pkt | 1 Pkt = {c_Pkt} Pcs
                              </div>
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>₹{rate.toFixed(2)}</td>
                            <td style={{ textAlign: "center" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4, minWidth: 260 }}>
                                    {/* Future Note: Remove CTN/PKT/PCS columns by wrapping in conditional here */}
                                    <div>
                                      <div style={{ fontSize: 9, fontWeight: 800, color: "var(--text-muted)", marginBottom: 2 }}>BAG</div>
                                      <input 
                                          type="number" 
                                          className="form-input"
                                          placeholder="0"
                                          min="0"
                                          step="any"
                                          value={items[sku]?.bag || ""} 
                                          onChange={e => {
                                            const val = Math.max(0, parseFloat(e.target.value) || 0);
                                            setItems({ ...items, [sku]: { ...(items[sku]||{}), bag: val || "" } });
                                          }}
                                          style={{ width: 68, textAlign: "center", fontWeight: 800, padding: 10, fontSize: 15 }}
                                      />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 9, fontWeight: 800, color: "var(--text-muted)", marginBottom: 2 }}>CTN</div>
                                      <input 
                                          type="number" 
                                          className="form-input"
                                          placeholder="0"
                                          min="0"
                                          step="any"
                                          value={items[sku]?.ctn || ""} 
                                          onChange={e => {
                                            const val = Math.max(0, parseFloat(e.target.value) || 0);
                                            setItems({ ...items, [sku]: { ...(items[sku]||{}), ctn: val || "" } });
                                          }}
                                          style={{ width: 68, textAlign: "center", fontWeight: 800, padding: 10, fontSize: 15 }}
                                      />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 9, fontWeight: 800, color: "var(--text-muted)", marginBottom: 2 }}>PKT</div>
                                      <input 
                                          type="number" 
                                          className="form-input"
                                          placeholder="0"
                                          min="0"
                                          step="any"
                                          value={items[sku]?.pkt || ""} 
                                          onChange={e => {
                                            const val = Math.max(0, parseFloat(e.target.value) || 0);
                                            setItems({ ...items, [sku]: { ...(items[sku]||{}), pkt: val || "" } });
                                          }}
                                          style={{ width: 68, textAlign: "center", fontWeight: 800, padding: 10, fontSize: 15 }}
                                      />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 9, fontWeight: 800, color: "var(--text-muted)", marginBottom: 2 }}>PCS</div>
                                      <input 
                                          type="number" 
                                          className="form-input"
                                          placeholder="0"
                                          min="0"
                                          step="any"
                                          value={items[sku]?.pcs || ""} 
                                          onChange={e => {
                                            const val = Math.max(0, parseFloat(e.target.value) || 0);
                                            setItems({ ...items, [sku]: { ...(items[sku]||{}), pcs: val || "" } });
                                          }}
                                          style={{ width: 68, textAlign: "center", fontWeight: 800, padding: 10, fontSize: 15 }}
                                      />
                                    </div>
                                </div>
                                {q > 0 && (c_Bag > 1 || c_Ctn > 1 || c_Pkt > 1) && (
                                  <div style={{ fontSize: 10, color: "var(--primary)", marginTop: 6, fontWeight: 700 }}>
                                    Total: {q.toFixed(2)} BAGS
                                  </div>
                                )}
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 800, verticalAlign: "middle" }}>₹{sub.toFixed(2)}</td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ position: "sticky", top: 100 }}>
            <div className="premium-card" style={{ background: "var(--text-main)", color: "white", padding: 28 }}>
              <h3 style={{ margin: "0 0 24px 0", fontSize: 13, fontWeight: 800, opacity: 0.6 }}>DISTRIBUTION SUMMARY</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ opacity: 0.7 }}>Basic Total</span>
                  <span style={{ fontWeight: 700 }}>₹{financialSummary.tBasic.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ opacity: 0.7 }}>Tax Amount</span>
                  <span style={{ fontWeight: 700 }}>₹{financialSummary.tGst.toFixed(2)}</span>
                </div>
                <div style={{ width: "100%", height: 1, background: "rgba(255,255,255,0.1)" }}></div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 32, fontWeight: 900, color: "var(--success)" }}>₹{financialSummary.tGrand.toFixed(2)}</div>
                    <div style={{ fontSize: 10, opacity: 0.5 }}>READY FOR INVOICING</div>
                </div>
                <button 
                    onClick={handleConfirmOrder}
                    disabled={submitting}
                    className="btn-primary"
                    style={{ background: "var(--success)", border: "none", width: "100%", padding: 18, fontSize: 16 }}
                >
                    {submitting ? "GENERATING INVOICE..." : "CONFIRM & DOWNLOAD"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CnFToDistributorBilling;
